// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { usePromotionStore } from '@/store/promotion.store';
import type {
  IPromotionBody,
  IPromotionUpdateBody,
} from '@/types/promotion.types';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------

/**
 * Hook pour gérer les mutations liées aux Promotion.
 * Fournit des fonctions pour créer, mettre à jour et supprimer des Promotion
 * avec gestion automatique des erreurs globales et du loader.
 */
export const PromotionMutation = () => {
  const PromotionStore = usePromotionStore();

  // -------------------------------
  // 🔹 Gestion centralisée des erreurs
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0] || 'Une erreur est survenue';

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un Promotion
  // -------------------------------
  const createPromotion = useMutation<any, Error, IPromotionBody>({
    mutationFn: (payload: IPromotionBody) => {
      return PromotionStore.createPromotion(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un curency
  // -------------------------------
  const updatePromotion = useMutation<any, Error, IPromotionUpdateBody>({
    mutationFn: (payload: IPromotionUpdateBody) => {
      return PromotionStore.updatePromotion(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un taxe
  // -------------------------------
  const deletePromotion = useMutation<any, Error>({
    mutationFn: () => PromotionStore.deletePromotion(),
    onSuccess: () => {},
    onError: handleError,
  });

  /**
   * Crée une nouvelle Promotion
   * @param {IPromotionBody} item - Données du Promotion
   */
  const handleCreatePromotion = (item: IPromotionBody) => {
    return createPromotion.mutateAsync(item);
  };
  /**
   * Met à jour un Promotion existant
   * @param {IPromotionUpdateBody} item - Données de mise à jour du taxe
   */
  const handleUpdatePromotion = (item: IPromotionUpdateBody) => {
    return updatePromotion.mutateAsync(item);
  };
  /**
   * Suppression d'un Promotion existant
   */
  const handleDeletePromotion = () => {
    return deletePromotion.mutateAsync();
  };

  return {
    handleCreatePromotion,
    handleUpdatePromotion,
    handleDeletePromotion,
  };
};
